import { 
  doc, 
  updateDoc, 
  serverTimestamp, 
  getDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs
} from "firebase/firestore";
import { db } from "./firebase";


/**
 * Notification service for managing user activity tracking and notification counts
 */

/**
 * Update user's last visit time for a specific page type
 * @param {string} userId - The user ID
 * @param {string} pageType - The page type ('opportunities', 'people', 'forum')
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateLastVisitTime = async (userId, pageType) => {
  try {
    if (!userId || !pageType) {
      const error = 'User ID and page type are required';
      return { success: false, error };
    }

    const validPageTypes = ['opportunities', 'people', 'forum'];
    if (!validPageTypes.includes(pageType)) {
      const error = `Invalid page type: ${pageType}. Must be one of: ${validPageTypes.join(', ')}`;
      return { success: false, error };
    }

    // Check if user profile exists before trying to update
    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      console.warn(`User profile not found for user: ${userId}`);
      return { success: false, error: 'User profile not found' };
    }

    const updateData = {
      [`lastVisitTimes.${pageType}`]: serverTimestamp(),
      lastUpdated: serverTimestamp()
    };

    await updateDoc(userRef, updateData);
    
    return { success: true };
  } catch (error) {
    console.error('Error updating visit time:', error.message);
    
    return { 
      success: false, 
      error: `Failed to update visit time: ${error.message}` 
    };
  }
};

/**
 * Update user's last login time
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateLastLoginTime = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const userRef = doc(db, 'user_profiles', userId);
    const updateData = {
      'lastVisitTimes.lastLogin': serverTimestamp(),
      lastLogin: serverTimestamp(),
      lastUpdated: serverTimestamp()
    };

    await updateDoc(userRef, updateData);

    return { success: true };
  } catch (error) {
    console.error('Error updating last login time:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's last visit times
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const getUserLastVisitTimes = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }

    const userData = userSnap.data();
    const lastVisitTimes = userData.lastVisitTimes || {};

    // Provide default timestamps if not set (use lastLogin or account creation)
    const defaultTime = userData.lastLogin || userData.createdAt || new Date();

    const result = {
      opportunities: lastVisitTimes.opportunities || defaultTime,
      people: lastVisitTimes.people || defaultTime,
      forum: lastVisitTimes.forum || defaultTime,
      lastLogin: lastVisitTimes.lastLogin || defaultTime
    };

    return {
      success: true,
      data: result
    };
  } catch (error) {
    console.error('Error getting user last visit times:', error.message);
    return { success: false, error: error.message };
  }
};

/**
 * Count new opportunities since last visit and return their IDs
 * @param {string} userId - The user ID
 * @param {Date} lastVisitTime - Last visit time for opportunities page
 * @returns {Promise<{success: boolean, count?: number, itemIds?: string[], error?: string}>}
 */
export const countNewOpportunities = async (userId, lastVisitTime) => {
  try {
    if (!userId || !lastVisitTime) {
      return { success: false, error: 'User ID and last visit time are required' };
    }

    const opportunitiesQuery = query(
      collection(db, 'startup_opportunities'),
      where('status', '==', 'active'),
      where('createdAt', '>', lastVisitTime),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(opportunitiesQuery);
    
    // Extract item IDs from the results
    const itemIds = snapshot.docs.map(doc => doc.id);
    
    return {
      success: true,
      count: snapshot.size,
      itemIds: itemIds
    };
  } catch (error) {
    console.error('Error counting new opportunities:', error.message);
    // Fallback to count-only mode on error
    try {
      const fallbackQuery = query(
        collection(db, 'startup_opportunities'),
        where('status', '==', 'active'),
        where('createdAt', '>', lastVisitTime)
      );
      const fallbackSnapshot = await getDocs(fallbackQuery);
      return { 
        success: true, 
        count: fallbackSnapshot.size, 
        itemIds: [] // Empty array indicates fallback mode
      };
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError.message);
      return { success: false, error: error.message, count: 0, itemIds: [] };
    }
  }
};

/**
 * Count new people since last visit and return their IDs
 * @param {string} userId - The user ID
 * @param {Date} lastVisitTime - Last visit time for people page
 * @returns {Promise<{success: boolean, count?: number, itemIds?: string[], error?: string}>}
 */
export const countNewPeople = async (userId, lastVisitTime) => {
  try {
    if (!userId || !lastVisitTime) {
      return { success: false, error: 'User ID and last visit time are required' };
    }

    const peopleQuery = query(
      collection(db, 'user_profiles'),
      where('onboardingComplete', '==', true),
      where('createdAt', '>', lastVisitTime),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(peopleQuery);
    
    // Filter out the current user and hidden users from the count
    const newPeople = snapshot.docs.filter(doc => {
      const data = doc.data();
      const isCurrentUser = doc.id === userId;
      const isHidden = data.isHidden;
      
      return !isCurrentUser && !isHidden;
    });
    
    // Extract item IDs from filtered results
    const itemIds = newPeople.map(doc => doc.id);
    
    return {
      success: true,
      count: newPeople.length,
      itemIds: itemIds
    };
  } catch (error) {
    console.error('Error counting new people:', error.message);
    // Fallback to count-only mode on error
    try {
      const fallbackQuery = query(
        collection(db, 'user_profiles'),
        where('onboardingComplete', '==', true),
        where('createdAt', '>', lastVisitTime)
      );
      const fallbackSnapshot = await getDocs(fallbackQuery);
      const fallbackCount = fallbackSnapshot.docs.filter(doc => 
        doc.id !== userId && !doc.data().isHidden
      ).length;
      return { 
        success: true, 
        count: fallbackCount, 
        itemIds: [] // Empty array indicates fallback mode
      };
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError.message);
      return { success: false, error: error.message, count: 0, itemIds: [] };
    }
  }
};

/**
 * Count new forum posts since last visit and return their IDs
 * @param {string} userId - The user ID
 * @param {Date} lastVisitTime - Last visit time for forum page
 * @returns {Promise<{success: boolean, count?: number, itemIds?: string[], error?: string}>}
 */
export const countNewForumPosts = async (userId, lastVisitTime) => {
  try {
    if (!userId || !lastVisitTime) {
      return { success: false, error: 'User ID and last visit time are required' };
    }

    // Count new questions - using correct collection name
    const questionsQuery = query(
      collection(db, 'forum_questions'),
      where('createdAt', '>', lastVisitTime),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );

    const questionsSnapshot = await getDocs(questionsQuery);
    
    // Filter out posts by the current user - using correct field name
    const newQuestions = questionsSnapshot.docs.filter(doc => {
      const data = doc.data();
      
      // Validate document structure
      if (!data.authorId) {
        console.warn('Question document missing authorId field:', doc.id);
        return false;
      }
      
      const isCurrentUser = data.authorId === userId;
      return !isCurrentUser;
    });

    // SIMPLIFIED: Only count questions, not replies
    // const repliesQuery = query(
    //   collection(db, 'forum_replies'),
    //   where('createdAt', '>', lastVisitTime),
    //   where('status', '==', 'active'),
    //   orderBy('createdAt', 'desc')
    // );

    // const repliesSnapshot = await getDocs(repliesQuery);
    
    // // Filter out replies by the current user - using correct field name
    // const newReplies = repliesSnapshot.docs.filter(doc => {
    //   const data = doc.data();
    //   
    //   // Validate document structure
    //   if (!data.authorId) {
    //     console.warn('Reply document missing authorId field:', doc.id);
    //     return false;
    //   }
    //   
    //   const isCurrentUser = data.authorId === userId;
    //   return !isCurrentUser;
    // });

    // Extract item IDs from questions only
    const questionIds = newQuestions.map(doc => doc.id);
    const itemIds = questionIds; // Only questions, no replies
    
    const totalCount = newQuestions.length; // Only count questions
    
    return {
      success: true,
      count: totalCount,
      itemIds: itemIds
    };
  } catch (error) {
    console.error('Error counting new forum posts:', error.message);
    // Fallback to count-only mode on error (questions only)
    try {
      const fallbackQuestionsSnapshot = await getDocs(query(
        collection(db, 'forum_questions'),
        where('createdAt', '>', lastVisitTime),
        where('status', '==', 'active')
      ));
      
      const fallbackQuestions = fallbackQuestionsSnapshot.docs.filter(doc => 
        doc.data().authorId && doc.data().authorId !== userId
      );
      
      const fallbackCount = fallbackQuestions.length; // Only questions
      return { 
        success: true, 
        count: fallbackCount, 
        itemIds: [] // Empty array indicates fallback mode
      };
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError.message);
      return { success: false, error: error.message, count: 0, itemIds: [] };
    }
  }
};

/**
 * Get all notification counts and new item IDs for a user
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, counts?: Object, newItems?: Object, error?: string}>}
 */
export const getAllNotificationCounts = async (userId) => {
  try {
    if (!userId) {
      return { 
        success: false, 
        error: 'User ID is required',
        counts: { opportunities: 0, people: 0, forum: 0 },
        newItems: { opportunities: [], people: [], forum: [] }
      };
    }

    // Check if user has completed onboarding - new users should see no notifications
    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      
      // If user hasn't completed onboarding, return zero counts for all categories
      if (userData.onboardingComplete !== true) {
        return {
          success: true,
          counts: { opportunities: 0, people: 0, forum: 0 },
          newItems: { opportunities: [], people: [], forum: [] }
        };
      }
    }

    // Get user's last visit times
    const visitTimesResult = await getUserLastVisitTimes(userId);
    if (!visitTimesResult.success) {
      return {
        success: false,
        error: visitTimesResult.error,
        counts: { opportunities: 0, people: 0, forum: 0 },
        newItems: { opportunities: [], people: [], forum: [] }
      };
    }

    const { opportunities: oppTime, people: peopleTime, forum: forumTime } = visitTimesResult.data;

    // Get counts and item IDs for each category
    const [oppResult, peopleResult, forumResult] = await Promise.all([
      countNewOpportunities(userId, oppTime),
      countNewPeople(userId, peopleTime),
      countNewForumPosts(userId, forumTime)
    ]);

    // Validate that counts match itemIds array lengths
    const validateResult = (result, category) => {
      if (!result.success) {
        console.warn(`${category} query failed:`, result.error);
        return { count: 0, itemIds: [] };
      }
      
      const count = result.count || 0;
      const itemIds = result.itemIds || [];
      
      // Check for synchronization issues
      if (itemIds.length > 0 && count !== itemIds.length) {
        console.warn(`Synchronization mismatch in ${category}:`, {
          count,
          itemIdsLength: itemIds.length
        });
        // Use itemIds length as the authoritative count
        return { count: itemIds.length, itemIds };
      }
      
      return { count, itemIds };
    };

    const oppData = validateResult(oppResult, 'opportunities');
    const peopleData = validateResult(peopleResult, 'people');
    const forumData = validateResult(forumResult, 'forum');

    const finalCounts = {
      opportunities: oppData.count,
      people: peopleData.count,
      forum: forumData.count
    };

    const finalNewItems = {
      opportunities: oppData.itemIds,
      people: peopleData.itemIds,
      forum: forumData.itemIds
    };


    return {
      success: true,
      counts: finalCounts,
      newItems: finalNewItems
    };
  } catch (error) {
    console.error('Error getting all notification counts:', error.message);
    return { 
      success: false, 
      error: error.message,
      counts: { opportunities: 0, people: 0, forum: 0 },
      newItems: { opportunities: [], people: [], forum: [] }
    };
  }
};

/**
 * Check if a specific item is new for the user
 * @param {Date} itemTimestamp - The timestamp when the item was created
 * @param {Date} lastVisitTime - The user's last visit time for that page type
 * @returns {boolean} True if the item is new
 */
export const isItemNew = (itemTimestamp, lastVisitTime) => {
  try {
    if (!itemTimestamp || !lastVisitTime) {
      return false;
    }

    // Convert Firestore timestamps to Date objects if needed
    let itemDate, visitDate;
    
    try {
      itemDate = itemTimestamp.toDate ? itemTimestamp.toDate() : new Date(itemTimestamp);
      visitDate = lastVisitTime.toDate ? lastVisitTime.toDate() : new Date(lastVisitTime);
    } catch (conversionError) {
      return false;
    }

    // Validate that we have valid dates
    if (isNaN(itemDate.getTime()) || isNaN(visitDate.getTime())) {
      return false;
    }

    return itemDate > visitDate;
  } catch (error) {
    console.error('Error in isItemNew:', error.message);
    // Fail gracefully - don't show badge if we can't determine status
    return false;
  }
};

/**
 * Reset user's visit times to early date (for testing)
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const resetUserVisitTimes = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const userRef = doc(db, 'user_profiles', userId);
    const earlyTime = new Date('2020-01-01');
    
    const updateData = {
      lastVisitTimes: {
        opportunities: earlyTime,
        people: earlyTime,
        forum: earlyTime,
        lastLogin: serverTimestamp()
      },
      lastUpdated: serverTimestamp()
    };

    await updateDoc(userRef, updateData);

    return { success: true };
  } catch (error) {
    console.error('Error resetting visit times:', error.message);
    return { success: false, error: error.message };
  }
};

/**
 * Initialize user's last visit times if they don't exist
 * @param {string} userId - The user ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const initializeUserVisitTimes = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    const userRef = doc(db, 'user_profiles', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return { success: false, error: 'User profile not found' };
    }

    const userData = userSnap.data();
    
    // Only initialize if lastVisitTimes doesn't exist
    if (!userData.lastVisitTimes) {
      // For all users, set visit times to current time so they only see new content going forward
      // This prevents overwhelming users with historical notifications
      const initialTime = new Date();
      
      const updateData = {
        lastVisitTimes: {
          opportunities: initialTime,
          people: initialTime,
          forum: initialTime,
          lastLogin: serverTimestamp()
        },
        lastUpdated: serverTimestamp(),
        // Add flag to track first-time initialization
        notificationPreferences: {
          autoMarkVisited: true,
          initializedAt: serverTimestamp()
        }
      };

      await updateDoc(userRef, updateData);
    }

    return { success: true };
  } catch (error) {
    console.error('Error initializing user visit times:', error.message);
    return { success: false, error: error.message };
  }
};